import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, useToast } from '@chakra-ui/react';
import { getSupabase, getSupabaseAdmin, supabaseSchema } from '../components/supabaseClient';
import { bucketName_files, tableName } from '../components/env';
import { sortObjectsByTime } from '../utils/utils';
import { hydrateScheduleRowsTimezoneSource, fetchViewerIanaTimezone } from '../utils/scheduleRowTimezone';
import { dbgLog } from '../utils/appDebugFlags';
import {
  COMBINED_UUID,
  buildCombinedDocPath,
  yjsPathFor,
} from './medStudentCombine';

const cLog = (...args) => dbgLog('medstudent-combine', ...args);

// Per-patient note path. Mirrors buildDocPath in usePatientDocument.js /
// MedStudentClinicPage.js — the individual note files we read as sources.
function buildPatientDocPath(row, schema) {
  const { patient_name, EHR_ID, id, date_ } = row;
  if (!date_) return null;
  const patientName = patient_name || 'Unknown';
  const patientId = EHR_ID || id;
  const fileName = `${patientName}-${patientId}.json`;
  const year = date_.split('-')[0];
  const schemaPrefix = schema && schema !== 'public' ? `${schema}/` : '';
  return `New/MedADocs/${schemaPrefix}${year}/${date_}/${fileName}`;
}

// "Combine Documents" for the med-student clinic. The logic lives in the
// useCombineDocuments hook so it can be triggered from anywhere (it moved
// from the top-bar green button into the document Tools menu, 2026-07-05);
// the default-export button remains for any legacy call sites.
//
// Merges the day's individual patient notes into ONE document, in the SAME
// order the sidebar/schedule lists them (earliest -> latest appointment time),
// and opens it inside MedStudentClinicPage.
//
// Works WITH live collab (MEDSTUDENT_LIVE_COLLAB): the med-student collab
// server keys a Yjs room by the document's storage path and, on first open of
// a room that has no `.yjs`, hydrates it from the `.json` envelope. So we:
//   1. read each patient's per-patient note `.json` in schedule order,
//   2. concatenate their Quill deltas under a per-patient H1 header,
//   3. write that combined envelope to the combined room path,
//   4. delete any stale `.yjs` sibling (which would otherwise win on
//      hydration and hide the freshly written content), and
//   5. navigate to /clinic/<COMBINED_UUID>?date=<date>.
// usePatientDocument recognizes that sentinel uuid and synthesizes the
// matching patient row so the room path lines up and the server hydrates from
// the combined doc we just wrote. See documentComponents/medStudentCombine.js.
export function useCombineDocuments() {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const combine = async (date) => {
    if (!date) {
      toast({
        title: 'No visit date',
        description: 'Cannot combine documents without a date in the URL.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const supabase = await getSupabase();
      const schema = supabaseSchema || 'public';

      // 1. Fetch the day's schedule rows, then order them EXACTLY like the
      //    sidebar (PatientListPanel): by the VIEWER's local appointment time
      //    (earliest -> latest), unscheduled last. sortObjectsByTime converts
      //    each row's stored wall-clock time_ into the viewer's timezone (via
      //    timezone_source + viewerTimezone) before comparing — the SAME
      //    conversion PatientCard uses to display it — so the combined document
      //    follows the on-screen order even when the schedule owner is in a
      //    different timezone than the viewer.
      const { data: rows, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('date_', date);
      if (error) throw error;

      const hydrated = await hydrateScheduleRowsTimezoneSource(supabase, rows || []);
      const viewerTimezone = await fetchViewerIanaTimezone(supabase);

      const scheduled = sortObjectsByTime(
        hydrated.filter((r) => r && typeof r.time_ === 'string'),
        'time_',
        { dateKey: 'date_', viewerTimezone },
      );
      const unscheduled = hydrated.filter((r) => !r || typeof r.time_ !== 'string');
      const ordered = [...scheduled, ...unscheduled];

      if (ordered.length === 0) {
        toast({
          title: 'Nothing to combine',
          description: `No visits scheduled for ${date}.`,
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        setIsLoading(false);
        return;
      }

      // 2. Download each patient's individual note (.json) in schedule order
      //    and concatenate their Quill deltas VERBATIM. Combine does not add,
      //    remove, or relabel any text — each note already carries its own
      //    title line, so we simply move the documents over in order. The only
      //    thing inserted between two notes is a single blank line (whitespace)
      //    so they don't run together.
      const finalOps = [];
      const seenPaths = new Set();
      let combinedCount = 0;

      for (const row of ordered) {
        const path = buildPatientDocPath(row, schema);
        // Skip rows with no derivable path, and de-dupe by file path: multiple
        // schedule rows for the same client on the same day (e.g. a morning and
        // an afternoon visit) resolve to ONE document file — combine it once,
        // not once per visit.
        if (!path || seenPaths.has(path)) continue;
        seenPaths.add(path);

        const { data: blob, error: dlErr } = await supabase.storage
          .from(bucketName_files)
          .download(path);
        if (dlErr || !blob) {
          cLog('skip (no note file):', path, dlErr?.message);
          continue;
        }

        let parsed;
        try {
          parsed = JSON.parse(await blob.text());
        } catch (e) {
          cLog('skip (unparseable json):', path);
          continue;
        }

        const ops = parsed?.delta?.ops;
        if (!Array.isArray(ops) || ops.length === 0) {
          cLog('skip (empty delta):', path);
          continue;
        }

        // Separate consecutive notes with one blank line (whitespace only — no
        // header, label, or divider text). The first note starts at the very
        // top with no leading blank line.
        if (combinedCount > 0) {
          finalOps.push({ insert: '\n' });
        }

        // Move the note's content over exactly as authored.
        finalOps.push(...ops);
        combinedCount += 1;
      }

      const finalDelta = { ops: finalOps };

      if (combinedCount === 0) {
        toast({
          title: 'Nothing to combine',
          description: 'No saved notes were found for this day yet.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
        setIsLoading(false);
        return;
      }

      // 3. Write the combined envelope, and clear any stale `.yjs` so the
      //    collab server re-hydrates the room from this fresh `.json`.
      //    Use the service-role admin client for the write — same client the
      //    med-student autosave uses for MedADocs (bypasses storage RLS).
      const admin = getSupabaseAdmin();
      const docPath = buildCombinedDocPath(date, schema);
      const yjsPath = yjsPathFor(docPath);

      const envelope = {
        delta: finalDelta,
        sections: [],
        lastUpdated: new Date().toISOString(),
        processedSources: [],
        _combined: true,
      };
      const body = new Blob([JSON.stringify(envelope)], { type: 'application/json' });

      // Delete stale .yjs first (a "not found" is fine — ignore it).
      try {
        await admin.storage.from(bucketName_files).remove([yjsPath]);
      } catch (e) {
        cLog('yjs remove ignored:', e?.message);
      }

      const { error: upErr } = await admin.storage
        .from(bucketName_files)
        .upload(docPath, body, { upsert: true, contentType: 'application/json' });
      if (upErr) throw upErr;

      cLog('wrote combined doc:', docPath, `(${combinedCount} notes)`);

      // 4. Open it in the med-student clinic page.
      navigate(`/clinic/${COMBINED_UUID}?date=${encodeURIComponent(date)}`);

      toast({
        title: 'Documents combined',
        description: `Merged ${combinedCount} note${combinedCount === 1 ? '' : 's'} in schedule order.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (e) {
      console.error('[MedStudentCombine] failed:', e);
      toast({
        title: 'Combine failed',
        description: e?.message || 'Unexpected error while combining documents.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { combine, isLoading };
}

export default function MedStudentCombineButton({ date }) {
  const { combine, isLoading } = useCombineDocuments();

  return (
    <Button
      colorScheme="green"
      onClick={() => combine(date)}
      isLoading={isLoading}
      loadingText="Combining"
    >
      Combine Documents
    </Button>
  );
}
